'use client';

import { useEffect, useState } from 'react';

import {
  resolveCartItemsScheduleId,
} from '@/app/(main)/_components/schedule-selection/_services/resolve-cart-items-schedule.service';
import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { getLogger } from '@/logger/logger';
import { CartItem } from '@/types/cart.types';

const logger = getLogger('schedule-selection.cart-items');

export interface UseCartItemsScheduleReturn {
  isLoading: boolean;
  scheduleId?: string;
  hasError: boolean;
}

const IDLE: UseCartItemsScheduleReturn = { isLoading: false, hasError: false };

export function useCartItemsSchedule(
  items: CartItem[] | undefined,
  scheduleType: ScheduleType | undefined,
): UseCartItemsScheduleReturn {
  const [state, setState] = useState<UseCartItemsScheduleReturn>(IDLE);

  const itemsKey = items?.map((item) => item.id).join('|') ?? '';

  useEffect(() => {
    if (!items || items.length === 0) {
      setState(IDLE);

      return;
    }

    if (!scheduleType) {
      setState({ isLoading: false, hasError: true });

      return;
    }

    let cancelled = false;

    setState({ isLoading: true, hasError: false });

    resolveCartItemsScheduleId(items, scheduleType)
      .then((scheduleId) => {
        if (cancelled) {
          return;
        }

        if (!scheduleId) {
          logger.error('No schedule matched the cart items being rescheduled', {
            cart: {
              event: 'missing_schedule_id',
              stage: 'slot_lookup',
              target: 'search_api',
              cart_item_ids: items.map((item) => item.id),
            },
          });
        }

        setState({ isLoading: false, scheduleId, hasError: !scheduleId });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        logger.error('Failed to read the schedule of the cart items being rescheduled', {
          cart: {
            event: 'slot_read_failed',
            stage: 'slot_lookup',
            target: 'search_api',
            cart_item_ids: items.map((item) => item.id),
          },
          error,
        });
        setState({ isLoading: false, hasError: true });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, scheduleType]);

  return state;
}
